import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Loader2, Heart, BookOpen, Crown, Leaf, Globe, Bell, Play, ExternalLink,
  ShoppingBag, MessageCircle, Mail, MapPin, Link as LinkIcon,
  Music2, Youtube, ShieldCheck,
} from "lucide-react";
import { api, formatApiError } from "../lib/api";
import { ShopHeader } from "../components/shop/ShopHeader";
import { toast } from "sonner";

const FILE_BASE = `${process.env.REACT_APP_BACKEND_URL}/api/files/`;
const PURPLE = "#5B2A9D";
const GOLD = "#C9A227";
const WA = "19172807564";
const DON_STRIPE_URL = "https://buy.stripe.com/6oU9AV4F52eI5mgg2LaZi03";
const BIBLE_APP_URL = "https://noumabou-bible.preview.emergentagent.com/lesson";
const ECOLE_EMBED_URL = "https://noumabou-bible.preview.emergentagent.com/api/embed/ecole";
const HERO_BG = "radial-gradient(120% 120% at 15% 0%, #4a2585 0%, #26124a 55%, #180a30 100%)";
const DARK_BG = "radial-gradient(120% 120% at 50% 0%, #3a1d6e 0%, #1a0c34 100%)";

function ytId(v) {
  if (!v) return "";
  const m = v.match(/(?:youtu\.be\/|v=|embed\/|shorts\/|live\/)([A-Za-z0-9_-]{11})/);
  return m ? m[1] : (v.length === 11 ? v : "");
}

function Eyebrow({ children, className = "", dark }) {
  return (
    <span className={`inline-flex items-center gap-3 text-xs font-bold tracking-[0.3em] uppercase ${className}`} style={{ color: GOLD }}>
      <span className="w-8 h-px" style={{ background: GOLD, opacity: dark ? 0.6 : 1 }} />
      {children}
    </span>
  );
}

function SectionTitle({ before, accent, dark }) {
  return (
    <h2 className={`font-display text-4xl sm:text-5xl font-black mt-4 ${dark ? "text-white" : "text-brand-ink"}`}>
      {before} <span className="italic" style={{ color: dark ? GOLD : PURPLE }}>{accent}</span>
    </h2>
  );
}

const VERSETS = [
  { t: "Car je connais les projets que j'ai formés sur vous, des projets de paix et non de malheur, afin de vous donner un avenir et de l'espérance.", r: "Jérémie 29:11" },
  { t: "Je puis tout par celui qui me fortifie.", r: "Philippiens 4:13" },
  { t: "L'Éternel est mon berger : je ne manquerai de rien.", r: "Psaume 23:1" },
  { t: "Cherchez premièrement le royaume et la justice de Dieu, et tout cela vous sera donné par-dessus.", r: "Matthieu 6:33" },
  { t: "Tout est possible à celui qui croit.", r: "Marc 9:23" },
  { t: "Remets ton sort à l'Éternel, mets en lui ta confiance, et il agira.", r: "Psaume 37:5" },
  { t: "Ne crains rien, car je suis avec toi ; ne promène pas des regards inquiets, car je suis ton Dieu.", r: "Ésaïe 41:10" },
  { t: "La foi est une ferme assurance des choses qu'on espère, une démonstration de celles qu'on ne voit pas.", r: "Hébreux 11:1" },
];

function VersetDuJour() {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI((x) => (x + 1) % VERSETS.length), 6000);
    return () => clearInterval(id);
  }, []);
  const v = VERSETS[i];
  return (
    <section className="py-14 border-t border-white/5" style={{ background: DARK_BG }} data-testid="verset-du-jour">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center text-white">
        <Eyebrow className="justify-center" dark>Verset du jour</Eyebrow>
        <div className="mt-6 min-h-[120px] flex flex-col items-center justify-center">
          <motion.p key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}
            className="font-display text-2xl sm:text-3xl italic leading-relaxed">« {v.t} »</motion.p>
          <p className="mt-4 font-bold tracking-wide" style={{ color: GOLD }}>— {v.r}</p>
        </div>
        <div className="flex justify-center gap-1.5 mt-6">
          {VERSETS.map((_, k) => <span key={k} className="w-1.5 h-1.5 rounded-full transition-colors" style={{ background: k === i ? GOLD : "rgba(255,255,255,0.25)" }} />)}
        </div>
      </div>
    </section>
  );
}

export default function Ministere() {
  const [site, setSite] = useState(null);

  useEffect(() => {
    api.get("/settings/site").then(({ data }) => setSite(data)).catch(() => setSite({}));
  }, []);

  if (!site) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin" style={{ color: PURPLE }} /></div>;

  const videos = (site.youtube_videos || []).map(ytId).filter(Boolean);
  const books = site.books || [];

  return (
    <div className="App overflow-x-hidden bg-white">
      <ShopHeader />

      {/* Hero */}
      <section className="relative text-white" style={{ background: HERO_BG }}>
        <img
          src="https://customer-assets.emergentagent.com/job_vital-store-1/artifacts/nfu8u877_867BA37A-687A-40EB-8E67-795E5BE0FEF6.PNG"
          alt="Noumabou Ministries — Le Royaume de Dieu révélé"
          className="w-full h-auto block"
          data-testid="ministere-hero-image"
        />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-12 -mt-4 sm:-mt-8">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="flex flex-wrap gap-4 justify-center">
            <a href="#livres" data-testid="hero-livres-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-brand-ink shadow-lg hover:brightness-105 transition-all" style={{ background: GOLD }}>
              <BookOpen className="w-5 h-5" /> Nos livres
            </a>
            <a href="#don" data-testid="hero-don-cta" className="inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-bold text-white border-2 border-white/25 hover:bg-white/10 transition-all">
              <Heart className="w-5 h-5" /> Soutenir la mission
            </a>
          </motion.div>
        </div>
      </section>

      <VersetDuJour />

      {/* Livres */}
      <section id="livres" className="bg-slate-50 py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <Eyebrow className="justify-center">Œuvres littéraires</Eyebrow>
            <SectionTitle before="Livres qui" accent="transforment" />
            <p className="text-slate-500 mt-3">Disponibles sur Amazon · Livraison mondiale.</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-8 mt-12">
            {books.length === 0 ? (
              <p className="text-slate-400 col-span-2 text-center">Livres à venir.</p>
            ) : books.map((b, i) => {
              const cover = b.cover_url || (b.cover_path ? FILE_BASE + b.cover_path : "");
              return (
                <motion.div key={i} data-testid={`book-${i}`} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="bg-white rounded-3xl border border-slate-200 shadow-sm overflow-hidden flex">
                  <div className="w-32 sm:w-40 shrink-0 flex items-center justify-center p-4" style={{ background: "linear-gradient(160deg,#241046,#3a1d6e)" }}>
                    {cover
                      ? <img src={cover} alt={b.title} className="w-full h-auto rounded-md shadow-lg" />
                      : <BookOpen className="w-12 h-12 text-white/85" />}
                  </div>
                  <div className="p-6 flex flex-col flex-1">
                    <span className="text-[11px] font-bold tracking-[0.2em] uppercase" style={{ color: PURPLE }}>Noumabou Ministries™</span>
                    <h3 className="font-display text-xl font-bold text-brand-ink mt-1">{b.title}</h3>
                    {b.subtitle && <p className="text-sm text-slate-500 mt-2 flex-1">{b.subtitle}</p>}
                    {b.amazon_url ? (
                      <a href={b.amazon_url} target="_blank" rel="noreferrer" data-testid={`book-amazon-${i}`} className="mt-5 inline-flex items-center justify-center gap-2 rounded-full py-3 font-bold text-white text-sm hover:brightness-95 transition-all" style={{ background: "#FF9900" }}>
                        <ShoppingBag className="w-4 h-4" /> Commander sur Amazon
                      </a>
                    ) : <span className="mt-5 text-xs text-slate-400 text-center py-3">Lien à venir</span>}
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Fondateur */}
      <section id="fondateur" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-10 items-center">
          <motion.div initial={{ opacity: 0, scale: 0.97 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ duration: 0.5 }}
            className="rounded-3xl overflow-hidden aspect-[4/5] max-w-md shadow-xl" style={{ background: PURPLE }}>
            <img src="/noumabou-author.jpeg" alt="Kossi Bogar Missekpe" className="w-full h-full object-cover" />
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5 }}>
            <Eyebrow>À propos du fondateur</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-black mt-4" style={{ color: PURPLE }}>Kossi Bogar<br />MISSEKPE</h2>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-slate-400 mt-3">Fondateur · Auteur · Ministre du Royaume</p>
            <p className="text-brand-sub leading-relaxed mt-6">{site.author_bio || "Kossi Bogar Missekpe est le fondateur de NOUMABOU Ministries™."}</p>
            <blockquote className="mt-6 pl-5 border-l-4 italic text-brand-ink" style={{ borderColor: GOLD }}>
              « Le Royaume de Dieu n'est pas une religion. C'est une réalité qui transforme la vie, équilibre le corps et change le monde entier. »
            </blockquote>
          </motion.div>
        </div>
      </section>

      {/* École du Royaume */}
      <section id="ecole" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <div className="rounded-3xl p-8 sm:p-12 text-white" style={{ background: DARK_BG }}>
          <div className="text-center">
            <Crown className="w-12 h-12 mx-auto" style={{ color: GOLD }} />
            <h2 className="font-display text-3xl sm:text-4xl font-bold mt-4">École du Royaume de Dieu</h2>
            <p className="text-white/70 mt-3 max-w-xl mx-auto">
              Découvrez les vérités profondes du Royaume de Dieu à travers des enseignements bibliques. Étudiez à votre rythme dans l'application.
            </p>
          </div>

          {/* Enseignements en direct depuis l'app Bible (embed) */}
          <div className="mt-9 rounded-2xl overflow-hidden border border-white/10 bg-white shadow-2xl" data-testid="ecole-embed">
            <iframe
              src={ECOLE_EMBED_URL}
              title="École du Royaume — enseignements"
              className="w-full"
              style={{ height: "760px", border: 0 }}
              loading="lazy"
              allow="autoplay; fullscreen; picture-in-picture"
            />
          </div>

          {videos.length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mt-6">
              {videos.map((id, i) => (
                <div key={i} className="rounded-2xl overflow-hidden aspect-video bg-black">
                  <iframe className="w-full h-full" src={`https://www.youtube.com/embed/${id}`} title={`video-${i}`} allowFullScreen loading="lazy" />
                </div>
              ))}
            </div>
          )}

          <div className="text-center mt-9 flex flex-wrap items-center justify-center gap-4">
            <a href={BIBLE_APP_URL} target="_blank" rel="noreferrer" data-testid="ecole-app-cta"
              className="inline-flex items-center gap-2 rounded-full px-8 py-3.5 font-bold text-brand-ink shadow-lg hover:brightness-105 transition-all" style={{ background: GOLD }}>
              <BookOpen className="w-5 h-5" /> Accéder aux enseignements
            </a>
            {site.youtube_channel_url && (
              <a href={site.youtube_channel_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-white/70 hover:text-white">
                <Youtube className="w-4 h-4" /> Voir la chaîne YouTube <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Notre Mission */}
      <section className="text-white py-20" style={{ background: DARK_BG }}>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Eyebrow className="justify-center" dark>Notre mission</Eyebrow>
          <SectionTitle before="Trois piliers," accent="une mission royale" dark />
          <p className="text-white/70 mt-4 max-w-2xl mx-auto">
            NOUMABOU Ministries™ transforme les individus, les familles et les nations pour la gloire de Dieu.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-12">
            {[
              { i: Crown, t: "Le Royaume dans la Vie", d: "Révéler les vérités du Royaume de Dieu pour transformer les vies." },
              { i: Leaf, t: "L'Équilibre dans le Corps", d: "Offrir des compléments alimentaires de haute qualité pour la santé." },
              { i: Globe, t: "L'Impact dans le Monde", d: "Toucher les nations à travers une présence internationale forte." },
            ].map((p, k) => (
              <motion.div key={k} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: k * 0.1 }}
                className="rounded-3xl p-8 bg-white/[0.06] border border-white/10 backdrop-blur-sm">
                <div className="w-14 h-14 rounded-2xl mx-auto flex items-center justify-center" style={{ background: `${GOLD}22` }}>
                  <p.i className="w-7 h-7" style={{ color: GOLD }} />
                </div>
                <h3 className="font-display text-xl font-bold mt-5">{p.t}</h3>
                <p className="text-white/60 text-sm mt-3">{p.d}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact */}
      <section id="contact" className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
        <div className="grid lg:grid-cols-2 gap-10">
          <div>
            <Eyebrow>Nous contacter</Eyebrow>
            <h2 className="font-display text-4xl sm:text-5xl font-black text-brand-ink mt-4">
              Contactez-<span style={{ color: PURPLE }}>nous</span>
            </h2>
            <div className="mt-8 space-y-4">
              {[
                { i: Mail, l: "Email", v: "mission@noumabou.org · info@noumabou.org" },
                { i: LinkIcon, l: "Website", v: "www.noumabou.org" },
                { i: MapPin, l: "Adresse", v: "785 Courtlandt Ave, NY Bronx 10451 USA" },
              ].map((c, k) => (
                <div key={k} className="flex items-start gap-4 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm border-l-4" style={{ borderLeftColor: PURPLE }}>
                  <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${PURPLE}12` }}>
                    <c.i className="w-5 h-5" style={{ color: PURPLE }} />
                  </span>
                  <div>
                    <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">{c.l}</p>
                    <p className="text-brand-ink font-medium mt-1 break-words">{c.v}</p>
                  </div>
                </div>
              ))}
              <div className="flex items-center gap-4 bg-white rounded-2xl border border-slate-200 p-5 shadow-sm border-l-4" style={{ borderLeftColor: GOLD }}>
                <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${GOLD}18` }}>
                  <Music2 className="w-5 h-5" style={{ color: GOLD }} />
                </span>
                <div className="flex-1">
                  <p className="text-[11px] font-bold tracking-[0.2em] uppercase text-slate-400">Réseaux sociaux</p>
                  <div className="flex gap-4 mt-2">
                    <a href="https://www.tiktok.com/@noumabou_togo" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-ink hover:text-brand"><Music2 className="w-4 h-4" /> TikTok</a>
                    <a href="https://www.youtube.com/@noumabouministries" target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-sm text-brand-ink hover:text-brand"><Youtube className="w-4 h-4" /> YouTube</a>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-8">
            <h3 className="font-display text-2xl font-bold text-brand-ink">Envoyez-nous un message</h3>
            <p className="text-slate-500 mt-2">Une question sur nos enseignements, nos livres ou nos produits ? Contactez-nous directement.</p>
            <a href={`https://wa.me/${WA}`} target="_blank" rel="noreferrer" data-testid="contact-whatsapp" className="mt-6 flex items-center gap-4 rounded-2xl px-6 py-4 text-white hover:brightness-105 transition-all" style={{ background: "#25D366" }}>
              <MessageCircle className="w-6 h-6" />
              <div><p className="font-bold">WhatsApp</p><p className="text-sm text-white/85">+1 917 280 7564</p></div>
            </a>
            <a href="mailto:info@noumabou.org" data-testid="contact-email" className="mt-4 flex items-center gap-4 rounded-2xl px-6 py-4 text-white hover:brightness-105 transition-all" style={{ background: PURPLE }}>
              <Mail className="w-6 h-6" />
              <div><p className="font-bold">Email</p><p className="text-sm text-white/85">info@noumabou.org</p></div>
            </a>
          </div>
        </div>
      </section>

      {/* Soutenir la mission (don) */}
      <section id="don" className="text-white py-20" style={{ background: DARK_BG }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <Eyebrow className="justify-center" dark>Soutenir la mission</Eyebrow>
          <SectionTitle before="Donnez pour" accent="soutenir la mission" dark />
          <p className="text-white/70 mt-4 max-w-xl mx-auto">
            Votre don soutient la proclamation du Royaume de Dieu, la création de contenu gratuit et l'impact de NOUMABOU Ministries™ à travers le monde.
          </p>

          <div className="grid grid-cols-3 gap-4 mt-10">
            {[{ i: BookOpen, t: "Enseignements gratuits" }, { i: Globe, t: "Impact international" }, { i: Crown, t: "Mission du Royaume" }].map((c, k) => (
              <div key={k} className="rounded-2xl p-5 bg-white/[0.06] border border-white/10">
                <c.i className="w-6 h-6 mx-auto" style={{ color: GOLD }} />
                <p className="text-sm font-semibold mt-3">{c.t}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-col items-center">
            <a href={DON_STRIPE_URL} target="_blank" rel="noreferrer" data-testid="donate-button"
              className="inline-flex items-center gap-2 rounded-full px-12 py-4 font-bold text-brand-ink shadow-lg hover:brightness-105 transition-all text-lg" style={{ background: GOLD }}>
              <Heart className="w-5 h-5" /> Donner maintenant
            </a>
            <p className="text-center text-xs text-white/50 mt-4 flex items-center justify-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5" /> Paiement 100% sécurisé via Stripe · Carte bancaire
            </p>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#140a26] text-white/70">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <div className="grid sm:grid-cols-3 gap-10">
            <div>
              <span className="font-display text-2xl font-black text-white">NOUMA<span style={{ color: GOLD }}>BOU</span></span>
              <p className="text-[11px] tracking-[0.3em] text-white/40 mt-1">MINISTRIES™</p>
              <p className="text-sm text-white/50 mt-4 max-w-xs">Le Royaume dans la vie. L'équilibre dans le corps. L'impact dans le monde. Organisation internationale — États-Unis &amp; Afrique.</p>
              <div className="flex gap-3 mt-5">
                <a href="https://www.tiktok.com/@noumabou_togo" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"><Music2 className="w-4 h-4" /></a>
                <a href="https://www.youtube.com/@noumabouministries" target="_blank" rel="noreferrer" className="w-9 h-9 rounded-lg bg-white/10 flex items-center justify-center hover:bg-white/20"><Youtube className="w-4 h-4" /></a>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">Navigation</p>
              <div className="flex flex-col gap-2 mt-4 text-sm">
                <a href="#fondateur" className="hover:text-white">Fondateur</a>
                <a href="#livres" className="hover:text-white">Livres</a>
                <a href="#ecole" className="hover:text-white">École du Royaume</a>
                <a href="#don" className="hover:text-white">Donner</a>
              </div>
            </div>
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-white/40">Contact</p>
              <div className="flex flex-col gap-2 mt-4 text-sm">
                <a href="mailto:mission@noumabou.org" className="hover:text-white">mission@noumabou.org</a>
                <a href="mailto:info@noumabou.org" className="hover:text-white">info@noumabou.org</a>
                <a href="https://www.noumabou.org" target="_blank" rel="noreferrer" className="hover:text-white">www.noumabou.org</a>
              </div>
            </div>
          </div>
          <div className="mt-10 pt-6 border-t border-white/10 text-xs text-white/40 text-center">
            © {new Date().getFullYear()} NOUMABOU Ministries™ — Tous droits réservés.
          </div>
        </div>
      </footer>
    </div>
  );
}
